import { catchError, distinctUntilChanged, filter, map, mergeMap, skip, switchMap, takeUntil } from 'rxjs/operators';
import { concat as concat$, defer, from as from$, of as of$, forkJoin, timer } from 'rxjs';

import EligibilityService from '../../services/eligibility.service';
import PayerService from '../../services/payer.service';
import RequestService from '../../services/request.service';
import CoverageService from '../../services/coverage.service';
import * as eligibilityActions from '../actions/eligibility.actions';
import * as etaActions from '../actions/eta.actions';
import { fetchAuditEntries, fetchAuditEntriesFailure, fetchAuditEntriesSuccess } from '../actions/eligibility.actions';
import createEligibilityData, {
  createMultiEligibilityData,
  validateMultiEligibility,
} from '../../parsers/eligibility.parser';
import { requestId, submitRequest } from './helpers/request.helper';
import * as requestsActions from '../actions/requests.actions';
import { errorPipe } from './epicsUtil';
import {
  resolveRequestRefreshDialog,
  resolveRequestRefreshResourcesToApply,
} from '../helpers/requestRefreshDialog.helper';
import { getPartialRequestRefreshActions } from './helpers/requestRefreshActions.helper';

const REQUEST_REFRESH_OVERLAY_MIN_DURATION_MS = 500;
const SELF_INITIATED_REFRESH_WINDOW_MS = 120000;

export const acceptEligibilityResultEpic = action$ =>
  action$.pipe(
    filter(eligibilityActions.acceptEligibilityResult.match),
    map(({ payload }) => ({ verificationId: payload.verificationId, requestId: payload.requestId })),
    switchMap(payload =>
      concat$(
        of$(eligibilityActions.setAcceptingEligibility(true)),
        from$(EligibilityService.acceptEligibilityResult(payload)).pipe(
          map(() => requestsActions.editRequest(payload.requestId)),
          errorPipe,
        ),
      ),
    ),
  );

export const clearAcceptingEligibilityEpic = (action$, store) =>
  action$.pipe(
    filter(requestsActions.editRequestSuccess.match),
    filter(() => store.value.eligibility.isAcceptingEligibility),
    map(() => eligibilityActions.setAcceptingEligibility(false)),
  );

export const requestRunStatusEpic = (action$, state$) =>
  action$.pipe(
    filter(
      action =>
        requestsActions.editRequestSuccess.match(action) ||
        requestsActions.createRequestSuccess.match(action) ||
        eligibilityActions.checkMultiEligibilitySuccess.match(action) ||
        eligibilityActions.error.match(action) ||
        (eligibilityActions.eligibilityAgentStatusReceived.match(action) &&
          action.payload.resource_id === state$.value.requests.request?.id &&
          ['completed', 'failed'].includes(action.payload.meta?.state)),
    ),
    filter(() => !!window.ambulnzConfig.constants.REQUIRE_EA_RUN_BEFORE_MANUAL_PREBILLER_VERIFIED),
    map(() => state$.value.requests.request?.id),
    filter(Boolean),
    map(requestId => eligibilityActions.fetchRunStatus(requestId)),
  );

export const fetchRunStatusEpic = action$ =>
  action$.pipe(
    filter(eligibilityActions.fetchRunStatus.match),
    switchMap(({ payload: requestId }) =>
      from$(EligibilityService.getInsuranceVerificationRunStatus(requestId)).pipe(
        map(({ hasRun }) => eligibilityActions.fetchRunStatusSuccess({ requestId, hasRun })),
        catchError(() => of$(eligibilityActions.fetchRunStatusFailure({ requestId }))),
      ),
    ),
  );

const fetchReportContent = response => {
  if (!response.reportUrl) return of$({ ...response, reportContent: '' });

  return from$(fetch(response.reportUrl).then(res => res.text())).pipe(
    map(reportContent => ({ ...response, reportContent })),
    catchError(() => of$({ ...response, reportContent: '' })),
  );
};

export const checkEligibilityEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.checkEligibility.match),
    map(({ payload }) => ({
      eligibilityData: createEligibilityData(
        store.value,
        payload.coordinationTypeId,
        payload.isAgentMode ? 'agent' : 'report',
      ),
      isAgentMode: payload.isAgentMode,
    })),
    switchMap(({ eligibilityData, isAgentMode }) =>
      from$(EligibilityService.checkEligibility(eligibilityData)).pipe(
        map(response => ({
          ...response,
          coordinationTypeId: eligibilityData.coordinationTypeId,
          isAgentMode,
        })),
        switchMap(response => fetchReportContent(response)),
      ),
    ),
    map(response => eligibilityActions.checkEligibilitySuccess(response)),
  );

export const loadPortalInfoEpic = action$ =>
  action$.pipe(
    filter(eligibilityActions.loadPortalInfo.match),
    switchMap(action => from$(PayerService.getPayerOptions(action.payload))),
    map(response => eligibilityActions.portalInfoLoaded(response)),
  );

export const acceptChangesEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.acceptChanges.match),
    mergeMap(() => {
      const state = store.value;
      const { changesModal } = state.eligibility;
      const { verificationId, coordinationTypeId } = changesModal;
      const reqId = requestId(store);
      const { coverages } = state.infobox;
      const coverage = coverages[coordinationTypeId];

      if (!reqId) {
        return of$(eligibilityActions.acceptChangesError('No active request ID found'));
      }

      if (!coverage) {
        return of$(eligibilityActions.acceptChangesError('Coverage not found'));
      }

      return concat$(
        of$(eligibilityActions.acceptEligibilityResult({ verificationId, requestId: reqId })),
        of$(eligibilityActions.acceptChangesSuccess()),
      );
    }),
  );

const fetchReportContents = response => {
  const coverages = response.coverages ?? [];

  if (!coverages.length) return of$(response);

  const fetchCoverage$ = coverage => {
    if (!coverage.reportUrl) return of$({ ...coverage, reportContent: '' });

    return from$(fetch(coverage.reportUrl).then(res => res.text())).pipe(
      map(reportContent => ({ ...coverage, reportContent })),
      catchError(() => of$({ ...coverage, reportContent: '' })),
    );
  };

  return forkJoin(coverages.map(fetchCoverage$)).pipe(
    map(enrichedCoverages => ({ ...response, coverages: enrichedCoverages })),
  );
};

const fetchLastVerification = requestId =>
  from$(EligibilityService.getLastInsuranceVerification(requestId)).pipe(
    switchMap(response => {
      if (!response) {
        return concat$(
          of$(eligibilityActions.setAgentProcessing(false)),
          of$(
            eligibilityActions.error({
              message: 'No insurance verifications found. Request is already biller verified.',
            }),
          ),
        );
      }
      return fetchReportContents(response).pipe(
        map(enrichedResponse =>
          eligibilityActions.checkMultiEligibilitySuccess({
            ...enrichedResponse,
            isBillerVerified: true,
          }),
        ),
      );
    }),
    catchError(error =>
      concat$(
        of$(eligibilityActions.setAgentProcessing(false)),
        of$(eligibilityActions.error(error)),
      ),
    ),
  );

const isRequestBillerVerifiedConflict = error => {
  let errorBody;
  try {
    errorBody = JSON.parse(error?.body || '{}').error || {};
  } catch {
    errorBody = {};
  }

  return errorBody.status === 409 && errorBody.code === 'request_is_biller_verified';
};

export const checkMultiEligibilityEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.checkMultiEligibility.match),
    switchMap(action => {
      const isBillerVerified = store.value.requests.isBillerVerified;

      if (isBillerVerified) {
        const reqId = store.value.requests.request?.id;
        if (!reqId) {
          return concat$(
            of$(eligibilityActions.setAgentProcessing(false)),
            of$(eligibilityActions.error({ message: 'No request ID found' })),
          );
        }
        return fetchLastVerification(reqId);
      }

      const validationReasons = validateMultiEligibility(store.value);
      if (validationReasons.length > 0) {
        return concat$(
          of$(eligibilityActions.setAgentProcessing(false)),
          of$(
            eligibilityActions.error({
              message: `Cannot run the Eligibility Agent. Please fix the following: ${validationReasons.join('; ')}.`,
            }),
          ),
        );
      }

      const performCheck = () => {
        const payload = createMultiEligibilityData(store.value, action.payload?.wandAttemptId);
        return from$(EligibilityService.checkMultiEligibility(payload)).pipe(
          switchMap(response => fetchReportContents(response)),
          map(response => eligibilityActions.checkMultiEligibilitySuccess(response)),
          catchError(error => {
            const reqId = store.value.requests.request?.id;

            if (reqId && isRequestBillerVerifiedConflict(error)) {
              return fetchLastVerification(reqId);
            }

            return concat$(
              of$(eligibilityActions.setAgentProcessing(false)),
              of$(eligibilityActions.error(error)),
            );
          }),
        );
      };

      const save$ = store.value.requests.request.id
        ? submitRequest(store).pipe(catchError(() => of$(null)))
        : of$(null);

      return save$.pipe(
        switchMap(() => performCheck()),
        catchError(error =>
          concat$(
            of$(eligibilityActions.setAgentProcessing(false)),
            of$(eligibilityActions.error(error)),
          ),
        ),
      );
    }),
  );

export const acceptMultiChangesEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.acceptMultiChanges.match),
    switchMap(() => {
      const { verificationId } = store.value.eligibility.agentView;
      const reqId = requestId(store);

      if (!verificationId) {
        return of$(eligibilityActions.acceptMultiChangesError('No verification ID found'));
      }
      if (!reqId) {
        return of$(eligibilityActions.acceptMultiChangesError('No request ID found'));
      }

      return concat$(
        of$(eligibilityActions.setAcceptingEligibility(true)),
        from$(EligibilityService.acceptInsuranceVerification(verificationId)).pipe(
          map(() => requestsActions.editRequest(reqId)),
          catchError(error =>
            concat$(
              of$(eligibilityActions.acceptMultiChangesError(error?.message || 'Failed to apply changes')),
              of$(eligibilityActions.setAcceptingEligibility(false)),
            ),
          ),
        ),
      );
    }),
  );

export const completeMultiAcceptEpic = (action$, store) =>
  action$.pipe(
    filter(requestsActions.editRequestSuccess.match),
    filter(() => store.value.eligibility.agentView.isLoading),
    mergeMap(() => of$(eligibilityActions.acceptMultiChangesSuccess(), eligibilityActions.resetAuditEntries())),
  );

const isAgentStatusFromCurrentUser = (payload, state) => {
  const statusUserId = Number(payload.user?.id);
  const currentUserId = Number(state.user?.model?.id);

  return !Number.isNaN(statusUserId) && statusUserId === currentUserId;
};

const shouldAutoClearAgentStatus = payload => payload.state === 'failed' || payload.state === 'completed';

export const eligibilityAgentStatusEpic = (action$, state$) =>
  action$.pipe(
    filter(eligibilityActions.eligibilityAgentStatusReceived.match),
    filter(({ payload }) => {
      const currentRequestId = state$.value.requests.request?.id;
      return payload.resource_id === currentRequestId;
    }),
    map(({ payload }) => {
      const meta = payload.meta || {};

      return eligibilityActions.setAgentStatus({
        state: meta.state,
        error: meta.error || null,
        changesApplied: meta.changesApplied || false,
        isCurrentUserUpdate: isAgentStatusFromCurrentUser(payload, state$.value),
      });
    }),
  );

export const refreshAgentViewOnStatusCompleteEpic = (action$, state$) =>
  action$.pipe(
    filter(eligibilityActions.eligibilityAgentStatusReceived.match),
    filter(({ payload }) => payload.resource_id === state$.value.requests.request?.id),
    filter(({ payload }) => payload.meta?.state === 'completed' && !!payload.meta?.changesApplied),
    filter(() => state$.value.eligibility.agentView.isOpen),
    map(({ payload }) => payload.meta.multiVerificationId),
    filter(multiVerificationId => !!multiVerificationId),
    switchMap(multiVerificationId =>
      from$(EligibilityService.getInsuranceVerification(multiVerificationId)).pipe(
        switchMap(response => fetchReportContents(response)),
        map(response => eligibilityActions.checkMultiEligibilitySuccess(response)),
        catchError(error => of$(eligibilityActions.error(error))),
      ),
    ),
  );

const getLatestRequestRefreshData = reqId =>
  forkJoin([
    defer(() => from$(RequestService.getRequest(reqId))),
    defer(() => from$(CoverageService.getCoverages(reqId))),
  ]).pipe(
    map(([response, coveragesResponse]) => ({
      ...response,
      coverages: coveragesResponse.coverages || [],
    })),
  );

export const prepareRequestRefreshDialogEpic = (action$, state$) =>
  action$.pipe(
    filter(eligibilityActions.setAgentStatus.match),
    filter(({ payload }) => payload.state === 'completed' && payload.changesApplied && !payload.isCurrentUserUpdate),
    switchMap(() => {
      const reqId = state$.value.requests.request?.id;

      if (!reqId) {
        return of$(eligibilityActions.clearAgentStatus());
      }

      // The auto-run reports under a system actor, so isCurrentUserUpdate can't tell it was the
      // current user's own Save & Get ETA — refresh silently instead of showing the dialog.
      // Scope to this request so a marker from one request never suppresses another's update.
      const marker = state$.value.eligibility?.selfInitiatedRun;
      const isSelfInitiated =
        !!marker && marker.requestId === reqId && Date.now() - marker.at <= SELF_INITIATED_REFRESH_WINDOW_MS;

      if (isSelfInitiated) {
        return of$(
          requestsActions.partialRefreshRequestAfterUpdate(reqId),
          eligibilityActions.markSelfInitiatedAgentRun(null),
        );
      }

      return getLatestRequestRefreshData(reqId).pipe(
        map(latestRequest =>
          requestsActions.showRequestRefreshDialog(resolveRequestRefreshDialog(state$.value, latestRequest)),
        ),
        catchError(() =>
          of$(requestsActions.showRequestRefreshDialog(resolveRequestRefreshDialog(state$.value, null))),
        ),
      );
    }),
  );

// Creating a request or clicking Save & Get ETA kicks off the request's own eligibility auto-run,
// so its system-attributed completion is self-initiated, not an external change. Record the request
// id the run will target: createRequestSuccess carries the new id (covers will-call and ETA creates),
// while clickGetETA on an existing request uses the current one.
const SELF_INITIATED_AGENT_RUN_TRIGGERS = [etaActions.clickGetETA, requestsActions.createRequestSuccess];

export const markSelfInitiatedAgentRunEpic = (action$, state$) =>
  action$.pipe(
    filter(action => SELF_INITIATED_AGENT_RUN_TRIGGERS.some(trigger => trigger.match(action))),
    map(action => {
      const requestId = requestsActions.createRequestSuccess.match(action)
        ? action.payload?.id
        : state$.value.requests.request?.id;

      return eligibilityActions.markSelfInitiatedAgentRun({ requestId: requestId ?? null, at: Date.now() });
    }),
  );

export const partialRefreshRequestAfterUpdateEpic = (action$, state$) =>
  action$.pipe(
    filter(requestsActions.partialRefreshRequestAfterUpdate.match),
    switchMap(({ payload }) => {
      const reqId = payload || state$.value.requests.request?.id;

      if (!reqId) return of$(eligibilityActions.clearAgentStatus());

      const refreshResult$ = getLatestRequestRefreshData(reqId).pipe(
        map(latestRequest => ({ status: 'success', latestRequest })),
        catchError(error => of$({ status: 'failure', error })),
      );

      return concat$(
        of$(eligibilityActions.setAcceptingEligibility(true)),
        forkJoin([refreshResult$, timer(REQUEST_REFRESH_OVERLAY_MIN_DURATION_MS)]).pipe(
          mergeMap(([result]) => {
            if (result.status === 'failure') {
              return concat$(
                of$(eligibilityActions.error(result.error)),
                of$(eligibilityActions.setAcceptingEligibility(false)),
              );
            }

            const resourcesToApply = resolveRequestRefreshResourcesToApply(state$.value, result.latestRequest);

            return concat$(
              of$(
                requestsActions.applyRequestPartialRefresh({
                  latestRequest: result.latestRequest,
                  resourcesToApply,
                }),
              ),
              of$(eligibilityActions.clearAgentStatus()),
              of$(eligibilityActions.setAcceptingEligibility(false)),
            );
          }),
        ),
      );
    }),
  );

export const applyRequestPartialRefreshEpic = (action$, state$) =>
  action$.pipe(
    filter(requestsActions.applyRequestPartialRefresh.match),
    mergeMap(({ payload }) => {
      if (!payload?.latestRequest) return of$();

      return of$(...getPartialRequestRefreshActions(payload.latestRequest, payload.resourcesToApply));
    }),
  );

export const agentStatusAutoClearEpic = action$ =>
  action$.pipe(
    filter(eligibilityActions.setAgentStatus.match),
    filter(({ payload }) => shouldAutoClearAgentStatus(payload)),
    switchMap(() =>
      timer(5000).pipe(
        map(() => eligibilityActions.clearAgentStatus()),
        takeUntil(
          action$.pipe(
            filter(a => eligibilityActions.setAgentStatus.match(a) || eligibilityActions.clearAgentStatus.match(a)),
          ),
        ),
      ),
    ),
  );

export const clearAgentStatusOnRequestChangeEpic = (_action$, state$) =>
  state$.pipe(
    map(state => state.requests.request?.id),
    distinctUntilChanged(),
    skip(1),
    map(() => eligibilityActions.clearAgentStatus()),
  );

export const rejectMultiChangesEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.rejectMultiChanges.match),
    switchMap(({ payload }) => {
      const { verificationId } = store.value.eligibility.agentView;
      const rejectReason = (payload?.rejectReason || '').trim();

      if (!verificationId) {
        return of$(eligibilityActions.rejectMultiChangesError('No verification ID found'));
      }
      if (!rejectReason) {
        return of$(eligibilityActions.rejectMultiChangesError('Reject reason is required'));
      }

      return from$(EligibilityService.rejectInsuranceVerification(verificationId, rejectReason)).pipe(
        mergeMap(() =>
          of$(eligibilityActions.rejectMultiChangesSuccess(), eligibilityActions.resetAuditEntries()),
        ),
        catchError(error =>
          of$(eligibilityActions.rejectMultiChangesError(error?.message || 'Failed to reject changes')),
        ),
      );
    }),
  );

export const rejectChangesEpic = (action$, store) =>
  action$.pipe(
    filter(eligibilityActions.rejectChanges.match),
    switchMap(({ payload }) => {
      const { verificationId } = store.value.eligibility.changesModal;
      const rejectReason = (payload?.rejectReason || '').trim();

      if (!verificationId) {
        return of$(eligibilityActions.rejectChangesError('No verification ID found'));
      }
      if (!rejectReason) {
        return of$(eligibilityActions.rejectChangesError('Reject reason is required'));
      }

      return from$(EligibilityService.rejectInsuranceVerification(verificationId, rejectReason)).pipe(
        map(() => eligibilityActions.rejectChangesSuccess()),
        catchError(error =>
          of$(eligibilityActions.rejectChangesError(error?.message || 'Failed to reject changes')),
        ),
      );
    }),
  );

export const fetchAuditLogEntriesEpic = (action$, store) =>
  action$.pipe(
    filter(fetchAuditEntries.match),
    switchMap(() => {
      const reqId = requestId(store);
      if (!reqId) {
        return of$(eligibilityActions.fetchAuditEntriesFailure('No request ID found'));
      }

      return from$(EligibilityService.insuranceVerifications(reqId)).pipe(
        map(entries => fetchAuditEntriesSuccess(entries)),
        catchError(err => of$(fetchAuditEntriesFailure(err.message))),
      );
    }),
  );
